// @ts-check
/**
 * The ordered next steps. The destination is built here from projectId, so the model can never point the
 * user at a URL of its own.
 *
 * Its linkLabel is deliberately ignored: the model writes text like 'Open Business View' or
 * 'View Compatibility', but every link goes to the project overview, so the label would promise a page
 * the link does not open. The label here names the project it opens, taken from metriQ's own project
 * list rather than from the report.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { TestableText } from '@components/general/TestableComponents';
import { projectTitle } from '@datahooks/ProjectsListContext';
import { renderReportMarkdown } from './AIReportMarkdown.jsx';
import { Link } from 'react-router-dom';

/** @typedef {import('@datahooks/types/organisationAiReport.types.js').AiReportInvestigationStep} AiReportInvestigationStep */
/** @typedef {import('@datahooks/ProjectsListContext').BasicProject} BasicProject */

/** Used when the project is not in the supplied names, so the label still matches the destination. */
export const PROJECT_LINK_FALLBACK = 'Open project overview';

/** @param {{ steps: AiReportInvestigationStep[], projects?: BasicProject[] }} props */
const AIReportSteps = ({ steps = [], projects = [] }) => {
  if (steps.length === 0) {
    return null;
  }

  return (
    <ol className="flex flex-col gap-4" data-testid="ai-report-steps">
      {steps.map((step, index) => (
        <li key={`${index}-${step.action}`} className="flex items-start gap-3" data-testid={`ai-report-step-${index}`}>
          <span
            aria-hidden="true"
            className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-gray-700"
          >
            {index + 1}
          </span>
          <div className="min-w-0">
            <TestableText variant="small" className="text-sm font-semibold text-gray-900 dark:text-gray-100" dataTestId={`ai-report-step-${index}-action`}>
              {step.action}
            </TestableText>
            <div
              className="mt-1 space-y-2 text-sm leading-6 text-gray-600 dark:text-gray-400"
              data-testid={`ai-report-step-${index}-detail`}
            >
              {renderReportMarkdown(step.detail, `ai-report-step-${index}-detail`)}
            </div>
            {step.projectId != null && (
              <Link
                to={`/projects/${step.projectId}`}
                className="mt-1 inline-block text-sm font-semibold text-blue-500 hover:underline"
                data-testid={`ai-report-step-${index}-link`}
              >
                {projectTitle(projects, step.projectId) ? `Open ${projectTitle(projects, step.projectId)}` : PROJECT_LINK_FALLBACK}
              </Link>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

AIReportSteps.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.number,
    action: PropTypes.string.isRequired,
    detail: PropTypes.string,
    linkLabel: PropTypes.string,
  })),
  projects: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.number,
    title: PropTypes.string,
  })),
};

export default AIReportSteps;
